import type { WorkspaceMenuAccess } from './workspaceMenuAccess';

/**
 * What the caller may do from a project's sidebar menus (spec §6.1 and §6.2, FR-2026-11-04-3).
 * Read from the workspace's server-computed access plus the project's own `projectManagerId`;
 * never inferred from the org role. Missing data grants nothing, so the menus fail closed.
 *
 * Levels: "Full edit" is the project's PM, a workspace owner or an org admin. "Edit" is the
 * default for any other workspace owner/member. Per-project View-only overrides do not exist
 * on the client yet (the Access dialog waits for its contract), so they cannot be read here.
 * Server routes remain the authority; these flags only decide what is offered.
 */
export type ProjectMenuAccess = {
  /** Rename, Custom fields, Import tasks, Phase, Browse templates: Full edit. */
  fullEdit: boolean;
  /** Duplicate, Archive, Delete, Invite member: an org admin or a workspace owner. */
  adminOrOwner: boolean;
  /** Task, Artifact (the "+" menu): Edit or above. */
  canEdit: boolean;
  /** The caller is this project's assigned project manager. */
  isProjectManager: boolean;
};

export function projectMenuAccess(
  workspace: WorkspaceMenuAccess,
  userId: string | null | undefined,
  projectManagerId: string | null | undefined,
): ProjectMenuAccess {
  const adminOrOwner = workspace.canAdminister || workspace.isOwner;
  // A PM still needs a write role in the workspace (a viewer PM is capped to View only).
  const isProjectManager =
    !!userId && !!projectManagerId && userId === projectManagerId && workspace.canCreate;
  const fullEdit = adminOrOwner || isProjectManager;
  return {
    fullEdit,
    adminOrOwner,
    canEdit: fullEdit || workspace.canCreate,
    isProjectManager,
  };
}
